import { Chart, ChartTypes, formatNumber, yAxisLabelProps } from "@/uiCore";
import BreakdownSkeleton from "./BreakdownSkeleton";
import dayjs from "dayjs";
import { CustomTooltip } from "@/components/graph/misc";
import { getKey, getTooltipNameValues } from "./utils";
import { Line, YAxis } from "recharts";
import { TotalCostEntities, BarColors } from "./constants";
import { AutoTuneSavingsSummaryResponse } from "../context/types";
import { useAppState } from "@/modules/app/useAppContext";

interface Props {
  isFetching: boolean;
  yearMode: boolean;
  data?: AutoTuneSavingsSummaryResponse;
  savingsType: string;
  currency?: string;
  aggregateBy: string;
  isFuture?: boolean;
  totalCostEntities: ReturnType<typeof TotalCostEntities>;
}

const AutonomousSavingsSection = ({
  isFetching,
  yearMode,
  data,
  savingsType,
  aggregateBy,
  isFuture,
  totalCostEntities,
}: Props) => {
  const { currency } = useAppState();

  if (isFetching) {
    return <BreakdownSkeleton type="chart" />;
  }
  if (!data) {
    return null;
  }

  const savingsFields =
    totalCostEntities[savingsType as keyof typeof totalCostEntities]?.fields || [];

  return (
    <Chart
      title="Autonomous Savings"
      xAxisDataKey="date"
      yAxisLabel={{
        value: "Savings",
      }}
      yAxisId="value"
      xAxisLabelFormatter={value =>
        `${dayjs(value).format(`${yearMode ? "MMM-YY" : "DD-MMM-YY"}`)}`
      }
      yAxisLabelFormatter={value => `${formatNumber(value, { currency })}`}
      type={ChartTypes.LineChart}
      data={data.graph}
      chartProps={{}}
      legendProps={{
        wrapperStyle: { bottom: -42 },
      }}
      fullScreenProps={{
        title: "Enter fullscreen",
        className: "!al-right-[-30px] !al-top-[-15px]",
      }}
      tooltipProps={{
        content: (
          // @ts-ignore TODO fix this
          <CustomTooltip
            timeRange={aggregateBy as "day" | "week" | "month"}
            nameValue={getTooltipNameValues(savingsType, totalCostEntities, currency)}
          />
        ),
      }}
    >
      {({ selectedLegend }) => (
        <>
          <YAxis
            yAxisId="number_of_decisions"
            orientation="right"
            label={{
              dx: -45,
              ...yAxisLabelProps,
              value: "Agent Decisions",
            }}
          />
          {savingsFields.map(d => {
            const k = getKey(d, isFuture);
            return (
              <Line
                key={k}
                yAxisId={k}
                type="monotone"
                name={d.label}
                dataKey={k}
                hide={Boolean(selectedLegend && selectedLegend !== k)}
                stroke={BarColors[k]}
                strokeWidth={5}
                dot={false}
              />
            );
          })}
        </>
      )}
    </Chart>
  );
};

export default AutonomousSavingsSection;
